import { MapCanvasValue } from "../components/map/contexts/MapCanvasContext/MapCanvasContext.types";
import {
  DEFAULT_WALL_COLOR,
  EDITING_ZONE_MAX_HEIGHT,
  EDITING_ZONE_MAX_WIDTH
} from "../components/map/defines";
import { SpotLabelPatternItem } from "../components/map/types";
import {
  RectangleShape,
  Shape,
  SpotShapeType,
  SVGShape,
  WallShape
} from "../types";
import { MAP_BUILDER_VERSION } from "../version";
import { generateShapeId } from "./generateShapeId";

interface PathSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Signed perpendicular displacement of the curve peak from the chord
   *  midpoint, along the normal (-dy, dx) — the builder's wall-curvature
   *  convention (see utils/bezier.ts). Set for curve commands only. */
  curvature?: number;
  /** Index of the subpath (M command run) this segment belongs to. */
  subpath?: number;
}

/** How many numbers each curve command consumes per repetition, and the
 *  offset of its endpoint x within that group. */
const CURVE_ARITY: Record<string, { size: number; endIndex: number }> = {
  C: { size: 6, endIndex: 4 },
  S: { size: 4, endIndex: 2 },
  Q: { size: 4, endIndex: 2 },
  T: { size: 2, endIndex: 0 },
  A: { size: 7, endIndex: 5 }
};

/**
 * Tokenize an SVG path `d` attribute into line segments.
 *
 * Handles the full command set (absolute and relative): M/L/H/V emit
 * segments directly, Z closes back to the subpath start, and curve
 * commands (C/S/Q/T/A) are approximated by their chord — walls exported
 * with rounded corners stay connected instead of leaving gaps (or worse,
 * feeding curve control points into the previous command and producing
 * NaN geometry, which the old MHVL-only tokenizer did).
 */
const parseSvgPathToSegments = (d: string): PathSegment[] => {
  const segments: PathSegment[] = [];
  const tokens = d.match(/[MmLlHhVvCcSsQqTtAaZz][^MmLlHhVvCcSsQqTtAaZz]*/g);
  if (!tokens) return segments;

  let cx = 0;
  let cy = 0;
  // Subpath start — where Z closes back to
  let sx = 0;
  let sy = 0;
  let subpath = -1;

  const pushSegment = (x1: number, y1: number, x2: number, y2: number) => {
    if (
      (x1 !== x2 || y1 !== y2) &&
      Number.isFinite(x1) &&
      Number.isFinite(y1) &&
      Number.isFinite(x2) &&
      Number.isFinite(y2)
    ) {
      segments.push({ x1, y1, x2, y2, subpath });
    }
  };

  for (const token of tokens) {
    const cmdChar = token[0];
    const cmd = cmdChar.toUpperCase();
    const isRelative = cmdChar !== cmd;
    const nums = token
      .slice(1)
      .trim()
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number);

    if (cmd === "M") {
      subpath++;
      // First pair moves; any extra pairs are implicit linetos.
      for (let i = 0; i + 1 < nums.length; i += 2) {
        const nx = isRelative ? cx + nums[i] : nums[i];
        const ny = isRelative ? cy + nums[i + 1] : nums[i + 1];
        if (i === 0) {
          sx = nx;
          sy = ny;
        } else {
          pushSegment(cx, cy, nx, ny);
        }
        cx = nx;
        cy = ny;
      }
    } else if (cmd === "L") {
      for (let i = 0; i + 1 < nums.length; i += 2) {
        const nx = isRelative ? cx + nums[i] : nums[i];
        const ny = isRelative ? cy + nums[i + 1] : nums[i + 1];
        pushSegment(cx, cy, nx, ny);
        cx = nx;
        cy = ny;
      }
    } else if (cmd === "H") {
      for (const n of nums) {
        const nx = isRelative ? cx + n : n;
        pushSegment(cx, cy, nx, cy);
        cx = nx;
      }
    } else if (cmd === "V") {
      for (const n of nums) {
        const ny = isRelative ? cy + n : n;
        pushSegment(cx, cy, cx, ny);
        cy = ny;
      }
    } else if (cmd in CURVE_ARITY) {
      const { size, endIndex } = CURVE_ARITY[cmd];
      for (let i = 0; i + size - 1 < nums.length; i += size) {
        const abs = (offset: number, base: number) =>
          isRelative ? base + nums[i + offset] : nums[i + offset];
        const nx = abs(endIndex, cx);
        const ny = abs(endIndex + 1, cy);

        // On-curve midpoint B(0.5) — turns the chord into a curved wall
        // edge instead of a flat approximation.
        let midX: number | null = null;
        let midY = 0;
        if (cmd === "C") {
          midX = (cx + 3 * abs(0, cx) + 3 * abs(2, cx) + nx) / 8;
          midY = (cy + 3 * abs(1, cy) + 3 * abs(3, cy) + ny) / 8;
        } else if (cmd === "Q") {
          midX = (cx + 2 * abs(0, cx) + nx) / 4;
          midY = (cy + 2 * abs(1, cy) + ny) / 4;
        } else if (cmd === "S") {
          // First control unknown here (reflection of the previous one) —
          // approximating it with the start point slightly understates
          // the bulge but keeps the bend.
          midX = (4 * cx + 3 * abs(0, cx) + nx) / 8;
          midY = (4 * cy + 3 * abs(1, cy) + ny) / 8;
        }

        let curvature: number | undefined;
        if (midX !== null) {
          const dx = nx - cx;
          const dy = ny - cy;
          const len = Math.hypot(dx, dy);
          if (len > 0) {
            const offset =
              (midX - (cx + nx) / 2) * (-dy / len) +
              (midY - (cy + ny) / 2) * (dx / len);
            if (Math.abs(offset) > 0.75) curvature = offset;
          }
        }

        if (
          (cx !== nx || cy !== ny) &&
          Number.isFinite(nx) &&
          Number.isFinite(ny)
        ) {
          segments.push({ x1: cx, y1: cy, x2: nx, y2: ny, curvature, subpath });
        }
        cx = nx;
        cy = ny;
      }
    } else if (cmd === "Z") {
      pushSegment(cx, cy, sx, sy);
      cx = sx;
      cy = sy;
    }
  }

  return segments;
};

/** Direct `<g>` children of an element. Equivalent to `:scope > g` but
 *  also works in XML DOMs where `:scope` matching is unreliable. */
const childGroups = (el: Element): Element[] =>
  Array.from(el.children).filter((c) => c.localName === "g");

// Coordinate tolerance for matching collinear segments
const WALL_TOLERANCE = 5;
// Gaps smaller than this are rendering artifacts, not doors
const MIN_DOOR_GAP = 20;
// Gaps larger than this are open spaces (corridors, etc.), not doors
const MAX_DOOR_GAP = 150;

interface LinearSegment {
  start: number;
  end: number;
  perpCoord: number;
  thickness: number;
}

/**
 * Parse raw wall path segments from the SVG document.
 * Looks for paths whose ID contains "Wall" (case-insensitive).
 */
// Filled wall bands wider than this are open areas, not a single wall.
// Exterior walls in legacy exports run up to ~50px thick.
const MAX_WALL_BAND = 60;

/**
 * Collapse a filled wall OUTLINE (a path tracing both sides of a thick
 * wall) into centerline segments with real thickness.
 *
 * Opposite sides pair up (parallel, within MAX_WALL_BAND, overlapping);
 * the centerline endpoint per end is the average of both sides' ends —
 * at a corner the outer side is longer by the band width, so the average
 * lands exactly half a thickness short and perpendicular centerlines
 * meet in the corner. End-cap stubs (the short closing sides of the
 * band) are dropped; the paired walls replace them.
 */
const collapseFilledWallOutline = (
  segments: PathSegment[]
): { segment: PathSegment; thickness: number }[] => {
  const result: { segment: PathSegment; thickness: number }[] = [];
  type Side = { start: number; end: number; perp: number; used: boolean };

  const horizontals: Side[] = [];
  const verticals: Side[] = [];
  const rest: PathSegment[] = [];

  // First pass — per subpath. Some exports draw each wall piece as its
  // own thin band subpath (with square/semicircle infills at joints, and
  // band extents ending exactly on the centerline corners). Those
  // collapse directly; anything else pools into the side-pairing pass.
  const bySubpath = new Map<number, PathSegment[]>();
  for (const seg of segments) {
    const key = seg.subpath ?? 0;
    const list = bySubpath.get(key);
    if (list) {
      list.push(seg);
    } else {
      bySubpath.set(key, [seg]);
    }
  }

  const pooled: PathSegment[] = [];
  for (const subSegments of bySubpath.values()) {
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    for (const seg of subSegments) {
      minX = Math.min(minX, seg.x1, seg.x2);
      minY = Math.min(minY, seg.y1, seg.y2);
      maxX = Math.max(maxX, seg.x1, seg.x2);
      maxY = Math.max(maxY, seg.y1, seg.y2);
    }
    const width = maxX - minX;
    const height = maxY - minY;
    const short = Math.min(width, height);
    const long = Math.max(width, height);

    if (short > MAX_WALL_BAND || !Number.isFinite(short)) {
      // Too wide for a single wall band — contour geometry, pool it
      pooled.push(...subSegments);
    } else if (long <= 2 * short + 6) {
      // Joint/end-cap infill (corner squares, rounded caps) — the
      // neighboring walls' own thickness covers this area
    } else if (width >= height) {
      const mid = minY + height / 2;
      result.push({
        segment: { x1: minX, y1: mid, x2: maxX, y2: mid },
        thickness: height
      });
    } else {
      const mid = minX + width / 2;
      result.push({
        segment: { x1: mid, y1: minY, x2: mid, y2: maxY },
        thickness: width
      });
    }
  }

  for (const seg of pooled) {
    if (seg.curvature) {
      rest.push(seg);
    } else if (seg.y1 === seg.y2) {
      horizontals.push({
        start: Math.min(seg.x1, seg.x2),
        end: Math.max(seg.x1, seg.x2),
        perp: seg.y1,
        used: false
      });
    } else if (seg.x1 === seg.x2) {
      verticals.push({
        start: Math.min(seg.y1, seg.y2),
        end: Math.max(seg.y1, seg.y2),
        perp: seg.x1,
        used: false
      });
    } else {
      rest.push(seg);
    }
  }

  const collapseAxis = (sides: Side[], axis: "horizontal" | "vertical") => {
    for (const side of sides) {
      if (side.used) continue;
      // Nearest overlapping anti-parallel side within the band limit
      let partner: Side | null = null;
      let partnerGap = MAX_WALL_BAND;
      for (const candidate of sides) {
        if (candidate === side || candidate.used) continue;
        const gap = Math.abs(candidate.perp - side.perp);
        if (gap === 0 || gap > partnerGap) continue;
        const overlap =
          Math.min(side.end, candidate.end) -
          Math.max(side.start, candidate.start);
        const shorter = Math.min(
          side.end - side.start,
          candidate.end - candidate.start
        );
        if (overlap < shorter * 0.5) continue;
        partnerGap = gap;
        partner = candidate;
      }
      if (!partner) continue;
      side.used = true;
      partner.used = true;
      const center = (side.perp + partner.perp) / 2;
      const from = (side.start + partner.start) / 2;
      const to = (side.end + partner.end) / 2;
      result.push({
        segment:
          axis === "horizontal"
            ? { x1: from, y1: center, x2: to, y2: center }
            : { x1: center, y1: from, x2: center, y2: to },
        thickness: partnerGap
      });
    }
    // Leftovers: short ones are band end-caps (drop); longer ones are
    // genuine thin walls — keep so nothing disappears.
    for (const side of sides) {
      if (side.used || side.end - side.start <= MAX_WALL_BAND) continue;
      result.push({
        segment:
          axis === "horizontal"
            ? { x1: side.start, y1: side.perp, x2: side.end, y2: side.perp }
            : { x1: side.perp, y1: side.start, x2: side.perp, y2: side.end },
        thickness: 4
      });
    }
  };

  collapseAxis(horizontals, "horizontal");
  collapseAxis(verticals, "vertical");
  for (const seg of rest) {
    result.push({ segment: seg, thickness: 4 });
  }
  return result;
};

const parseRawWallSegments = (
  doc: Document
): { segment: PathSegment; thickness: number }[] => {
  const result: { segment: PathSegment; thickness: number }[] = [];
  const paths = doc.querySelectorAll("path");

  // Some maps put anonymous "Vector N" paths inside a group named
  // "Walls" (or "Int/Ext Walls") instead of naming each path. Only the
  // bare group name qualifies — "Cubical Desks & Walls" is desk visuals,
  // not wall geometry.
  const isInsideWallsGroup = (path: Element): boolean => {
    let current = parentOf(path);
    while (current) {
      const groupId = (current.getAttribute("id") || "").trim().toLowerCase();
      if (/^(int(erior)?|ext(erior)?)?\s*walls?$/.test(groupId)) return true;
      current = parentOf(current);
    }
    return false;
  };

  for (const path of paths) {
    const id = path.getAttribute("id") || "";
    if (!id.toLowerCase().includes("wall") && !isInsideWallsGroup(path)) {
      continue;
    }

    const d = path.getAttribute("d");
    if (!d) continue;

    const fill = path.getAttribute("fill");
    const stroke = path.getAttribute("stroke");
    // Stroked path → the data IS the centerline. Filled path with no
    // stroke → the data traces the wall's outline (thick walls in some
    // legacy maps) and must be collapsed back to centerlines.
    const isFilledOutline =
      !!fill && fill !== "none" && (!stroke || stroke === "none");

    const segments = parseSvgPathToSegments(d);
    if (isFilledOutline) {
      result.push(...collapseFilledWallOutline(segments));
    } else {
      const thickness = Number.parseFloat(
        path.getAttribute("stroke-width") || "16"
      );
      for (const seg of segments) {
        result.push({ segment: seg, thickness });
      }
    }
  }

  return result;
};

/**
 * Parse door path segments from the SVG document.
 * Doors are identified by:
 * 1. Being inside <g id="Doors">
 * 2. Having stroke="#E8ECEF" (background color) on standalone paths
 */
const parseDoorSegments = (doc: Document): PathSegment[] => {
  const segments: PathSegment[] = [];
  const processedPaths = new Set<Element>();

  // 1. Paths inside Doors group
  const doorsGroup =
    doc.querySelector('g[id="Doors"]') || doc.querySelector('g[id="doors"]');
  if (doorsGroup) {
    for (const path of doorsGroup.querySelectorAll("path")) {
      const d = path.getAttribute("d");
      if (d) {
        segments.push(...parseSvgPathToSegments(d));
        processedPaths.add(path);
      }
    }
  }

  // 2. Standalone paths with #E8ECEF stroke (not walls, not inside resource/icon groups)
  const excludeGroups = [
    doc.querySelector('g[id="resources"]'),
    doc.querySelector('g[id="Resources"]'),
    doc.querySelector('g[id="Icons"]'),
    doc.querySelector('g[id="icons"]')
  ].filter(Boolean) as Element[];

  for (const path of doc.querySelectorAll("path")) {
    if (processedPaths.has(path)) continue;
    const id = (path.getAttribute("id") || "").toLowerCase();
    if (id.includes("wall")) continue;
    // Skip paths inside resource or icon groups
    if (excludeGroups.some((g) => g.contains(path))) continue;
    const stroke = (path.getAttribute("stroke") || "").toUpperCase();
    if (stroke === "#E8ECEF") {
      const d = path.getAttribute("d");
      if (d) {
        segments.push(...parseSvgPathToSegments(d));
      }
    }
  }

  return segments;
};

/**
 * Classify a segment as horizontal or vertical and normalize (start < end).
 */
const classifySegment = (
  seg: PathSegment
): {
  axis: "horizontal" | "vertical" | "diagonal";
  start: number;
  end: number;
  perpCoord: number;
} => {
  if (Math.abs(seg.y1 - seg.y2) < WALL_TOLERANCE) {
    return {
      axis: "horizontal",
      start: Math.min(seg.x1, seg.x2),
      end: Math.max(seg.x1, seg.x2),
      perpCoord: (seg.y1 + seg.y2) / 2
    };
  }
  if (Math.abs(seg.x1 - seg.x2) < WALL_TOLERANCE) {
    return {
      axis: "vertical",
      start: Math.min(seg.y1, seg.y2),
      end: Math.max(seg.y1, seg.y2),
      perpCoord: (seg.x1 + seg.x2) / 2
    };
  }
  return { axis: "diagonal", start: 0, end: 0, perpCoord: 0 };
};

/**
 * Group segments by collinear lines (same axis + same perpendicular coordinate within tolerance).
 * Returns a Map where each key is a representative perpCoord and value is the segments on that line.
 */
const groupByLine = <T extends { perpCoord: number }>(
  segments: T[]
): Map<number, T[]> => {
  const groups = new Map<number, T[]>();

  for (const seg of segments) {
    let matched = false;
    for (const [key, group] of groups) {
      if (Math.abs(seg.perpCoord - key) <= WALL_TOLERANCE) {
        group.push(seg);
        matched = true;
        break;
      }
    }
    if (!matched) {
      groups.set(seg.perpCoord, [seg]);
    }
  }

  return groups;
};

/**
 * Merge collinear wall segments that have doors in their gaps into single walls
 * with WallDoor entries. Also handles doors overlapping continuous walls.
 *
 * Algorithm:
 * 1. Group walls and doors by collinear lines (h/v)
 * 2. For each line, sort wall segments by position
 * 3. Walk through consecutive walls — if the gap is filled by a door segment,
 *    merge into one wall with a WallDoor entry
 * 4. Standalone doors overlapping a continuous wall also become WallDoor entries
 */
const parseSvgWallsAndDoors = (doc: Document): WallShape[] => {
  const rawWalls = parseRawWallSegments(doc);
  const rawDoors = parseDoorSegments(doc);

  // Classify wall segments by axis
  const hWalls: LinearSegment[] = [];
  const vWalls: LinearSegment[] = [];
  const diagonalWalls: WallShape[] = [];

  for (const { segment, thickness } of rawWalls) {
    // Curved runs stay individual walls carrying their curvature — the
    // wall-path migration chains them into a single path whose edges
    // bend, instead of a string of tiny straight pieces. They must not
    // enter the collinear merge (it would flatten them).
    if (segment.curvature) {
      diagonalWalls.push({
        id: generateShapeId("wall"),
        type: "wall",
        x: segment.x1,
        y: segment.y1,
        x2: segment.x2,
        y2: segment.y2,
        width: 0,
        height: 0,
        thickness,
        curvature: segment.curvature,
        curvatureCenter: 0.5,
        color: DEFAULT_WALL_COLOR,
        layerType: "shapes"
      });
      continue;
    }
    const classified = classifySegment(segment);
    if (classified.axis === "horizontal") {
      hWalls.push({
        start: classified.start,
        end: classified.end,
        perpCoord: classified.perpCoord,
        thickness
      });
    } else if (classified.axis === "vertical") {
      vWalls.push({
        start: classified.start,
        end: classified.end,
        perpCoord: classified.perpCoord,
        thickness
      });
    } else {
      // Diagonal walls can't have doors — keep as-is
      diagonalWalls.push({
        id: generateShapeId("wall"),
        type: "wall",
        x: segment.x1,
        y: segment.y1,
        x2: segment.x2,
        y2: segment.y2,
        width: 0,
        height: 0,
        thickness,
        color: DEFAULT_WALL_COLOR,
        layerType: "shapes"
      });
    }
  }

  // Classify door segments by axis
  type DoorLinear = { start: number; end: number; perpCoord: number };
  const hDoors: DoorLinear[] = [];
  const vDoors: DoorLinear[] = [];

  for (const seg of rawDoors) {
    const classified = classifySegment(seg);
    if (classified.axis === "horizontal") {
      hDoors.push({
        start: classified.start,
        end: classified.end,
        perpCoord: classified.perpCoord
      });
    } else if (classified.axis === "vertical") {
      vDoors.push({
        start: classified.start,
        end: classified.end,
        perpCoord: classified.perpCoord
      });
    }
  }

  // Merge walls with doors for each axis
  const mergedH = mergeWallLineGroups(hWalls, hDoors, "horizontal");
  const mergedV = mergeWallLineGroups(vWalls, vDoors, "vertical");

  const allWalls = [...mergedH, ...mergedV, ...diagonalWalls];

  // Second door pass: doors spanning the gap between two wall ENDPOINTS —
  // openings in curved/diagonal runs or across corners, which the
  // collinear merge above can't see. Each becomes a bridging wall segment
  // that is entirely a door, so the wall-path migration chains the runs
  // into one path with the opening integrated. Doors consumed by the
  // collinear merge sit in the interior of a merged wall and match no
  // endpoint, so they're naturally excluded.
  const DOOR_ENDPOINT_TOLERANCE = 8;
  const usedEndpoints = new Set<string>();
  const findFreeEndpoint = (
    px: number,
    py: number
  ): { wall: WallShape; ep: "start" | "end"; x: number; y: number } | null => {
    let best: {
      wall: WallShape;
      ep: "start" | "end";
      x: number;
      y: number;
    } | null = null;
    let bestDistance = DOOR_ENDPOINT_TOLERANCE;
    for (const wall of allWalls) {
      for (const ep of ["start", "end"] as const) {
        if (usedEndpoints.has(`${wall.id}:${ep}`)) continue;
        const x = ep === "start" ? wall.x : wall.x2;
        const y = ep === "start" ? wall.y : wall.y2;
        const dist = Math.hypot(px - x, py - y);
        if (dist <= bestDistance) {
          bestDistance = dist;
          best = { wall, ep, x, y };
        }
      }
    }
    return best;
  };

  for (const door of rawDoors) {
    const doorLength = Math.hypot(door.x2 - door.x1, door.y2 - door.y1);
    if (doorLength < MIN_DOOR_GAP || doorLength > MAX_DOOR_GAP) continue;
    const from = findFreeEndpoint(door.x1, door.y1);
    const to = findFreeEndpoint(door.x2, door.y2);
    if (!from || !to) continue;
    if (from.wall === to.wall && from.ep === to.ep) continue;
    usedEndpoints.add(`${from.wall.id}:${from.ep}`);
    usedEndpoints.add(`${to.wall.id}:${to.ep}`);
    allWalls.push({
      id: generateShapeId("wall"),
      type: "wall",
      x: from.x,
      y: from.y,
      x2: to.x,
      y2: to.y,
      width: 0,
      height: 0,
      thickness: from.wall.thickness,
      // Curved door strokes (following a curved wall) keep their arc —
      // the bridge runs door.p1 → p2, same direction the curvature was
      // measured in.
      curvature: door.curvature,
      curvatureCenter: door.curvature ? 0.5 : undefined,
      doors: [
        {
          id: generateShapeId("wall"),
          position: 0.5,
          width: doorLength
        }
      ],
      color: DEFAULT_WALL_COLOR,
      layerType: "shapes"
    });
  }

  return allWalls;
};

/**
 * For a given axis (horizontal/vertical), group walls and doors by collinear lines
 * and merge wall segments that have doors in their gaps.
 */
const mergeWallLineGroups = (
  walls: LinearSegment[],
  doors: DoorLinear[],
  axis: "horizontal" | "vertical"
): WallShape[] => {
  const wallLines = groupByLine(walls);
  const result: WallShape[] = [];

  for (const [lineCoord, lineWalls] of wallLines) {
    // Sort walls by start position
    lineWalls.sort((a, b) => a.start - b.start);

    // Find doors on this same line
    const lineDoors = doors.filter(
      (d) => Math.abs(d.perpCoord - lineCoord) <= WALL_TOLERANCE
    );

    // Cluster consecutive walls where gaps are filled by doors
    let clusterWalls: LinearSegment[] = [lineWalls[0]];
    let clusterDoors: DoorLinear[] = [];

    for (let i = 1; i < lineWalls.length; i++) {
      const prevEnd = clusterWalls[clusterWalls.length - 1].end;
      const currStart = lineWalls[i].start;
      const gapSize = currStart - prevEnd;

      // Look for a door that covers this gap
      const doorInGap =
        gapSize >= MIN_DOOR_GAP && gapSize <= MAX_DOOR_GAP
          ? lineDoors.find(
              (d) =>
                d.start <= prevEnd + WALL_TOLERANCE &&
                d.end >= currStart - WALL_TOLERANCE
            )
          : undefined;

      if (doorInGap) {
        // Door fills this gap — extend the cluster
        clusterDoors.push({
          start: prevEnd,
          end: currStart,
          perpCoord: doorInGap.perpCoord
        });
        clusterWalls.push(lineWalls[i]);
      } else {
        // No door in gap — finalize current cluster, start a new one
        result.push(
          ...finalizeWallCluster(clusterWalls, clusterDoors, lineCoord, axis)
        );
        clusterWalls = [lineWalls[i]];
        clusterDoors = [];
      }
    }

    // Finalize last cluster
    result.push(
      ...finalizeWallCluster(clusterWalls, clusterDoors, lineCoord, axis)
    );
  }

  return result;
};

type DoorLinear = { start: number; end: number; perpCoord: number };

/**
 * Finalize a cluster of wall segments (possibly with doors in gaps between them)
 * into one or more WallShape objects.
 */
const finalizeWallCluster = (
  walls: LinearSegment[],
  gapDoors: DoorLinear[],
  lineCoord: number,
  axis: "horizontal" | "vertical"
): WallShape[] => {
  if (walls.length === 1 && gapDoors.length === 0) {
    // Single wall, no merging needed
    const w = walls[0];
    return [createWallShape(w.start, w.end, lineCoord, w.thickness, axis)];
  }

  // Merge: wall spans from first start to last end
  const mergedStart = walls[0].start;
  const mergedEnd = walls[walls.length - 1].end;
  const thickness = walls[0].thickness;
  const mergedWall = createWallShape(
    mergedStart,
    mergedEnd,
    lineCoord,
    thickness,
    axis
  );

  // Create WallDoor entries from the gaps
  const wallLength = mergedEnd - mergedStart;
  if (wallLength > 0 && gapDoors.length > 0) {
    mergedWall.doors = gapDoors.map((gap) => {
      const doorMid = (gap.start + gap.end) / 2;
      const doorWidth = gap.end - gap.start;
      return {
        id: generateShapeId("wall"),
        position: Math.max(
          0,
          Math.min(1, (doorMid - mergedStart) / wallLength)
        ),
        width: doorWidth
      };
    });
  }

  return [mergedWall];
};

/**
 * Helper to create a WallShape from linear coordinates.
 */
const createWallShape = (
  start: number,
  end: number,
  lineCoord: number,
  thickness: number,
  axis: "horizontal" | "vertical"
): WallShape => ({
  id: generateShapeId("wall"),
  type: "wall",
  x: axis === "horizontal" ? start : lineCoord,
  y: axis === "horizontal" ? lineCoord : start,
  x2: axis === "horizontal" ? end : lineCoord,
  y2: axis === "horizontal" ? lineCoord : end,
  width: 0,
  height: 0,
  thickness,
  color: DEFAULT_WALL_COLOR,
  layerType: "shapes"
});

const DEFAULT_SPOT_SIZE = 100;

/**
 * Parse a transform attribute and extract the equivalent matrix components.
 * Handles: matrix(a,b,c,d,e,f), matrix(a b c d e f), translate(x,y), translate(x y)
 * Returns null if no supported transform is found.
 */
const parseTransformAsMatrix = (
  el: Element
): {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
} | null => {
  const transform = el.getAttribute("transform");
  if (!transform) return null;

  // Try matrix() — supports both comma and space separators
  const matrixMatch = transform.match(
    /matrix\(\s*([^\s,)]+)[\s,]+([^\s,)]+)[\s,]+([^\s,)]+)[\s,]+([^\s,)]+)[\s,]+([^\s,)]+)[\s,]+([^\s,)]+)\s*\)/
  );
  if (matrixMatch) {
    return {
      a: Number.parseFloat(matrixMatch[1]),
      b: Number.parseFloat(matrixMatch[2]),
      c: Number.parseFloat(matrixMatch[3]),
      d: Number.parseFloat(matrixMatch[4]),
      e: Number.parseFloat(matrixMatch[5]),
      f: Number.parseFloat(matrixMatch[6])
    };
  }

  // Try translate(x, y) or translate(x y)
  const translateMatch = transform.match(
    /translate\(\s*([^\s,)]+)[\s,]+([^\s,)]+)\s*\)/
  );
  if (translateMatch) {
    return {
      a: 1,
      b: 0,
      c: 0,
      d: 1,
      e: Number.parseFloat(translateMatch[1]),
      f: Number.parseFloat(translateMatch[2])
    };
  }

  return null;
};

/**
 * Apply a matrix transform to a point.
 */
const applyMatrix = (
  matrix: { a: number; b: number; c: number; d: number; e: number; f: number },
  x: number,
  y: number
): { x: number; y: number } => ({
  x: matrix.a * x + matrix.c * y + matrix.e,
  y: matrix.b * x + matrix.d * y + matrix.f
});

/**
 * Center (in the shape's own coordinates) of a round `<path>` or
 * `<ellipse>`. Some Figma exports draw the reservation dot this way
 * ("Reservation Node" paths) instead of as a `<circle>`.
 */
const roundShapeCenter = (
  shape: Element
): { cx: number; cy: number } | null => {
  if (shape.localName === "ellipse") {
    const rx = Number.parseFloat(shape.getAttribute("rx") || "0");
    const ry = Number.parseFloat(shape.getAttribute("ry") || "0");
    if (rx <= 0 || ry <= 0 || Math.abs(rx - ry) > Math.max(rx, ry) * 0.1) {
      return null;
    }
    return {
      cx: Number.parseFloat(shape.getAttribute("cx") || "0"),
      cy: Number.parseFloat(shape.getAttribute("cy") || "0")
    };
  }
  return circlePathGeometry(shape.getAttribute("d") || "");
};

/**
 * The reservation dot of a resource group: its `<circle>`, or else the
 * first round `<path>`/`<ellipse>`. Returns the element and its local
 * center (before transforms).
 */
const findReservationDot = (
  el: Element,
  isDirectChildOnly = false
): { dot: Element; cx: number; cy: number } | null => {
  const candidates = isDirectChildOnly
    ? Array.from(el.children)
    : Array.from(el.querySelectorAll("circle,path,ellipse"));
  const circle = candidates.find((c) => c.localName === "circle");
  if (circle) {
    return {
      dot: circle,
      cx: Number.parseFloat(circle.getAttribute("cx") || "0"),
      cy: Number.parseFloat(circle.getAttribute("cy") || "0")
    };
  }
  for (const shape of candidates) {
    if (shape.localName !== "path" && shape.localName !== "ellipse") continue;
    const center = roundShapeCenter(shape);
    if (center) return { dot: shape, ...center };
  }
  return null;
};

/**
 * Find the reservation dot within a group, traversing child groups.
 * Returns its center coordinates (with transforms applied).
 */
const findCircleCenter = (el: Element): { cx: number; cy: number } | null => {
  const found = findReservationDot(el);
  if (!found) return null;

  // Full transform chain dot → el, including rotate() — rotations
  // about an external center shift the dot's position.
  const center = applyComposed(
    composeTransformChain(found.dot, el),
    found.cx,
    found.cy
  );
  return { cx: center.x, cy: center.y };
};

/**
 * Extract a label from a resource group ID.
 * - Pure number IDs (e.g. "25") → use directly as label
 * - "Workspace 3.0_2" style → extract suffix after last underscore
 * - Other → use empty string (backend will fill from resource data)
 */
const extractLabel = (groupId: string): string => {
  // Pure number → use as label
  if (/^\d+$/.test(groupId)) return groupId;

  // "5211_2" → the label is 5211; the _2 is a Figma de-duplication
  // suffix, not the label.
  const dedupMatch = groupId.match(/^(\d+)_\d+$/);
  if (dedupMatch) return dedupMatch[1];

  // "Workspace 3.0_2" style → try label from Label_* child path ID
  // or extract suffix after last underscore
  const underscoreIdx = groupId.lastIndexOf("_");
  if (underscoreIdx !== -1) {
    const suffix = groupId.slice(underscoreIdx + 1);
    if (/^\d+$/.test(suffix)) return suffix;
  }

  return "";
};

/**
 * Find the resource_visual rect (the legacy desk rectangle) within a
 * resource group and return its center in absolute map coordinates, with
 * matrix/translate transforms applied — same walk as findCircleCenter.
 */
// The legacy renderer paints unstyled desks with this fill — anything
// else on the resource_visual rect is a manually assigned desk color.
const LEGACY_DEFAULT_DESK_FILL = "#E8ECEF";

const findResourceVisualRectCenter = (
  resourceGroup: Element
): {
  cx: number;
  cy: number;
  rotationDeg: number;
  customColor?: string;
} | null => {
  const rect = resourceGroup.querySelector('rect[id^="resource_visual"]');
  if (!rect) return null;
  const width = Number.parseFloat(rect.getAttribute("width") || "0");
  const height = Number.parseFloat(rect.getAttribute("height") || "0");
  if (width <= 0 || height <= 0) return null;

  const fill = rect.getAttribute("fill");
  const customColor =
    fill && fill.toUpperCase() !== LEGACY_DEFAULT_DESK_FILL && fill !== "none"
      ? fill
      : undefined;

  const localCx = Number.parseFloat(rect.getAttribute("x") || "0") + width / 2;
  const localCy = Number.parseFloat(rect.getAttribute("y") || "0") + height / 2;

  // Compose the full transform chain rect → resourceGroup. Round-floor
  // maps rotate every desk rect (rotate(angle cx cy)) — a matrix-only
  // walk would compute the UNROTATED center and misplace the spot.
  const matrix = composeTransformChain(rect, resourceGroup);
  const { x: cx, y: cy } = applyComposed(matrix, localCx, localCy);
  // The desk's angle — carried onto the spot so the glyph faces the
  // same way. Figma also places angled desks with mirrored matrices
  // (rotation + flip); a flipped rect looks identical, so only the angle
  // matters. It is folded into (-90°, 90°] because a 180° turn draws the
  // same rect but would put the label upside down.
  const det = matrix.a * matrix.d - matrix.b * matrix.c;
  let rotationDeg = (Math.atan2(matrix.b, matrix.a) * 180) / Math.PI;
  if (det < 0) {
    if (rotationDeg > 90) rotationDeg -= 180;
    if (rotationDeg <= -90) rotationDeg += 180;
  }

  return { cx, cy, rotationDeg, customColor };
};

/**
 * Detect desk orientation from where the chair circle sits relative to
 * the desk rectangle — both in absolute coordinates.
 */
const detectDeskOrientation = (
  rectCenter: { cx: number; cy: number },
  circleCenter: { cx: number; cy: number }
): "top" | "bottom" | "left" | "right" => {
  const dx = circleCenter.cx - rectCenter.cx;
  const dy = circleCenter.cy - rectCenter.cy;
  if (Math.abs(dy) >= Math.abs(dx)) {
    return dy < 0 ? "top" : "bottom";
  }
  return dx < 0 ? "left" : "right";
};

// Matched on the trimmed id — some exports carry stray whitespace in the
// id attribute, which an attribute selector would miss.
const hasResoChild = (group: Element): boolean =>
  childGroups(group).some((g) =>
    (g.getAttribute("id") || "").trim().startsWith("reso_")
  );

// A group holding its own reso_* child (or reservation dot) IS a resource
const isResourceGroup = (group: Element): boolean =>
  hasResoChild(group) || findReservationDot(group, true) !== null;

// Template containers ("Meeting Room (not used)", "Unused MR") hold
// placeholder resources — their visuals are preserved as vector shapes,
// but they must not produce bookable spots.
const isUnusedGroup = (group: Element): boolean => {
  const groupId = (group.getAttribute("id") || "").toLowerCase();
  return groupId.includes("not used") || groupId.includes("unused");
};

// A wrapper sits above the containers (e.g. resources > resources_2 >
// Desks > 90): one of its children holds linked resources. Only reso_*
// children count, so a room whose chairs are circle groups never reads as
// a container and gets unpacked into chair spots.
const isWrapperGroup = (group: Element): boolean =>
  !isResourceGroup(group) &&
  childGroups(group).some(
    (child) => !isResourceGroup(child) && childGroups(child).some(hasResoChild)
  );

// The groups under `resources` whose children are the resources (Desks,
// MR, …), descending through wrapper groups and leaving out unused ones
const collectResourceContainers = (parent: Element): Element[] =>
  childGroups(parent).flatMap((group) => {
    if (isUnusedGroup(group)) return [];
    return isWrapperGroup(group) ? collectResourceContainers(group) : [group];
  });

/**
 * Parse resource spots (desks, meeting rooms) from <g id="resources">.
 */
const parseSvgSpots = (doc: Document): Shape[] => {
  const spots: Shape[] = [];
  const resourcesGroup =
    doc.querySelector('g[id="resources"]') ||
    doc.querySelector('g[id="Resources"]');
  if (!resourcesGroup) return spots;

  // Process each container group (Desks, Meeting Room, etc.)
  for (const group of collectResourceContainers(resourcesGroup)) {
    const groupId = (group.getAttribute("id") || "").toLowerCase();
    // "Meeting Room" in older templates, bare "MR" in newer ones
    const isMeetingRoom = groupId.includes("meeting") || /^mr\b/.test(groupId);
    const spotType: SpotShapeType = isMeetingRoom
      ? SpotShapeType.Room
      : SpotShapeType.Desk;

    // Usually the group is a container (Desks/MR) whose children are the
    // resources — but some maps put resource groups DIRECTLY under
    // `resources`. Iterating a resource's own children would lose its
    // label and link.
    const resourceGroups = isResourceGroup(group)
      ? [group]
      : childGroups(group);
    for (const resourceGroup of resourceGroups) {
      const rawId = resourceGroup.getAttribute("id") || "";
      // Placeholder entries from the Figma template, not real resources
      if (/not used|replace with/i.test(rawId)) continue;
      const label = extractLabel(rawId);

      // Find the resource ID from reso_ prefixed child group. Matched on
      // the trimmed id — some exports carry stray whitespace in the id
      // attribute, which an attribute selector would miss.
      let resourceId: string | undefined;
      const resoGroup = Array.from(resourceGroup.querySelectorAll("g")).find(
        (g) => (g.getAttribute("id") || "").trim().startsWith("reso_")
      );
      if (resoGroup) {
        resourceId = (resoGroup.getAttribute("id") || "").trim() || undefined;
      }

      // Find circle center for positioning
      const circleCenter = findCircleCenter(resourceGroup);
      if (!circleCenter) continue;

      // The legacy desk rectangle (when present) determines appearance
      // and is the alignment anchor — the builder's desk glyph renders a
      // fixed 90×50 rect, so matching rect centers keeps the desk on the
      // legacy desk even when chair distances differ.
      const rectCenter = findResourceVisualRectCenter(resourceGroup);

      // Desks without a resource_visual rect are just circles (node appearance)
      const spotAppearance =
        !isMeetingRoom && !rectCenter ? SpotShapeType.Node : undefined;

      // Rotated desks (round-floor maps angle every desk) carry their
      // angle onto the spot so the glyph faces the same way.
      const spotRotation =
        rectCenter && Math.abs(rectCenter.rotationDeg) > 1
          ? Math.round(rectCenter.rotationDeg * 100) / 100
          : 0;

      // Desk orientation: which side of the desk the chair circle sits
      // on — measured in the DESK's local frame, so a rotated desk still
      // puts the chair dot on the correct side of the rotated glyph.
      let orientation: "top" | "bottom" | "left" | "right" = "top";
      if (rectCenter) {
        let frameCircle = circleCenter;
        if (spotRotation !== 0) {
          const rad = (-spotRotation * Math.PI) / 180;
          const dx = circleCenter.cx - rectCenter.cx;
          const dy = circleCenter.cy - rectCenter.cy;
          frameCircle = {
            cx: rectCenter.cx + dx * Math.cos(rad) - dy * Math.sin(rad),
            cy: rectCenter.cy + dx * Math.sin(rad) + dy * Math.cos(rad)
          };
        }
        orientation = detectDeskOrientation(rectCenter, frameCircle);
      }

      // Offsets from the spot origin to the rendered glyph, per
      // getOrientationLayout (SpotShape/helpers.ts): desk rect center is
      // (45,65)/(45,35)/(65,45)/(25,45) for top/bottom/left/right; the
      // node circle sits at (45,0) and the room circle at (70,60).
      const GLYPH_RECT_CENTER_OFFSET = {
        top: { x: 45, y: 65 },
        bottom: { x: 45, y: 35 },
        left: { x: 65, y: 45 },
        right: { x: 25, y: 45 }
      } as const;
      let spotX: number;
      let spotY: number;
      if (isMeetingRoom) {
        spotX = circleCenter.cx - 70;
        spotY = circleCenter.cy - 60;
      } else if (!rectCenter) {
        spotX = circleCenter.cx - 45;
        spotY = circleCenter.cy;
      } else {
        // Place the origin so the glyph's rect center — after the glyph
        // rotates about the origin — lands on the desk's true center.
        const offset = GLYPH_RECT_CENTER_OFFSET[orientation];
        if (spotRotation !== 0) {
          const rad = (spotRotation * Math.PI) / 180;
          spotX =
            rectCenter.cx -
            (offset.x * Math.cos(rad) - offset.y * Math.sin(rad));
          spotY =
            rectCenter.cy -
            (offset.x * Math.sin(rad) + offset.y * Math.cos(rad));
        } else {
          spotX = rectCenter.cx - offset.x;
          spotY = rectCenter.cy - offset.y;
        }
      }

      const spot: Shape = {
        id: generateShapeId("spot"),
        type: "spot",
        spotAppearance,
        orientation,
        labelPlacement: "on-rect",
        spotType,
        textAlign: "center",
        x: spotX,
        y: spotY,
        rotation: spotRotation !== 0 ? spotRotation : undefined,
        // Non-default legacy desk fill = a manually assigned desk color —
        // carry it over so the color picker shows/keeps it.
        color: rectCenter?.customColor,
        colorSource: rectCenter?.customColor ? "manual" : undefined,
        label,
        textColor: "#6B6B6B",
        name: `${isMeetingRoom ? "Meeting Room" : "Desk"} ${label}`,
        width: DEFAULT_SPOT_SIZE,
        height: DEFAULT_SPOT_SIZE,
        spacing: 2,
        rowSpacing: 5,
        rowsExtraSpacing: 20,
        layerType: "spots",
        rectProps: {
          cornerRadius: 2,
          opacity: 1
        },
        resource_id: resourceId
      };

      spots.push(spot);
    }
  }

  return spots;
};

/**
 * Parse a rotation transform from a rect element.
 */
const parseRotation = (el: Element): number | undefined => {
  const transform = el.getAttribute("transform");
  if (!transform) return undefined;

  const match = transform.match(
    /rotate\(\s*([^,)]+)(?:,\s*([^,)]+),\s*([^)]+))?\)/
  );
  if (!match) return undefined;

  return Number.parseFloat(match[1]);
};

/**
 * True when a rect's placement can't be expressed as x/y + rotation
 * (matrix placement, translate offsets, or rotation about a center other
 * than the rect's own corner) — such rects are preserved as vector
 * shapes instead of semantic rectangles, which would misplace them.
 */
const needsVectorPreservation = (rect: Element): boolean => {
  const transform = rect.getAttribute("transform") || "";
  if (!transform) return false;
  if (transform.includes("matrix(") || transform.includes("translate(")) {
    return true;
  }
  const rotate = transform.match(
    /rotate\(\s*[-\d.]+(?:[ ,]+([-\d.]+)[ ,]+([-\d.]+))?\s*\)/
  );
  if (rotate?.[1] !== undefined) {
    const x = Number.parseFloat(rect.getAttribute("x") || "0");
    const y = Number.parseFloat(rect.getAttribute("y") || "0");
    if (
      Math.abs(Number.parseFloat(rotate[1]) - x) > 1 ||
      Math.abs(Number.parseFloat(rotate[2] ?? "") - y) > 1
    ) {
      return true;
    }
  }
  return false;
};

/** Walk ancestors (up to and excluding stopAt) looking for a group whose
 *  id matches. */
const hasAncestorGroupId = (
  el: Element,
  matches: (id: string) => boolean,
  stopAt?: Element | null
): boolean => {
  let current = parentOf(el);
  while (current && current !== stopAt) {
    if (matches((current.getAttribute("id") || "").toLowerCase())) return true;
    current = parentOf(current);
  }
  return false;
};

/**
 * Parse rectangle shapes from the SVG for background/filler/table elements.
 */
const parseSvgRectangles = (doc: Document): RectangleShape[] => {
  const rectangles: RectangleShape[] = [];

  // Parse Layered background/desk rects. Scanned at ANY depth — some
  // maps nest the desk rects in a wrapper group — but skipping subtrees
  // that other importers own (stairs are preserved wholesale, legends
  // are excluded, doors are wall openings).
  const layeredGroup = doc.querySelector('g[id="Layered"]');
  const structureFallback = layeredGroup
    ? null
    : doc.querySelector('g[id="Structure"]');
  if (layeredGroup) {
    const rects = Array.from(layeredGroup.querySelectorAll("rect")).filter(
      (c) =>
        !needsVectorPreservation(c) &&
        !hasAncestorGroupId(
          c,
          (id) => /^(stairs|legend|doors)/.test(id),
          layeredGroup
        )
    );
    for (const rect of rects) {
      const x = Number.parseFloat(rect.getAttribute("x") || "0");
      const y = Number.parseFloat(rect.getAttribute("y") || "0");
      const width = Number.parseFloat(rect.getAttribute("width") || "0");
      const height = Number.parseFloat(rect.getAttribute("height") || "0");
      const fill = rect.getAttribute("fill") || "#E8ECEF";
      const rotation = parseRotation(rect);

      if (width > 0 && height > 0) {
        rectangles.push({
          id: generateShapeId("rectangle"),
          type: "rectangle",
          x,
          y,
          width,
          height,
          color: fill,
          layerType: "floor",
          rotation
        });
      }
    }
  }

  // Maps without a Layered group: only Structure's DIRECT rect children
  // qualify (a deep scan would double-import furniture visuals).
  if (structureFallback) {
    const rects = Array.from(structureFallback.children).filter(
      (c) => c.localName === "rect" && !needsVectorPreservation(c)
    );
    for (const rect of rects) {
      const x = Number.parseFloat(rect.getAttribute("x") || "0");
      const y = Number.parseFloat(rect.getAttribute("y") || "0");
      const width = Number.parseFloat(rect.getAttribute("width") || "0");
      const height = Number.parseFloat(rect.getAttribute("height") || "0");
      const fill = rect.getAttribute("fill") || "#E8ECEF";
      const rotation = parseRotation(rect);

      if (width > 0 && height > 0) {
        rectangles.push({
          id: generateShapeId("rectangle"),
          type: "rectangle",
          x,
          y,
          width,
          height,
          color: fill,
          layerType: "floor",
          rotation
        });
      }
    }
  }

  // Parse Filler rects (columns, structural elements) — including
  // "Filler_2"-style numbered variants.
  const fillerGroups = Array.from(doc.querySelectorAll("g[id]")).filter((g) =>
    /^filler/i.test(g.getAttribute("id") || "")
  );
  for (const fillerGroup of fillerGroups) {
    const rects = Array.from(fillerGroup.querySelectorAll("rect")).filter(
      (c) => !needsVectorPreservation(c)
    );
    for (const rect of rects) {
      const x = Number.parseFloat(rect.getAttribute("x") || "0");
      const y = Number.parseFloat(rect.getAttribute("y") || "0");
      const width = Number.parseFloat(rect.getAttribute("width") || "0");
      const height = Number.parseFloat(rect.getAttribute("height") || "0");
      const fill = rect.getAttribute("fill") || "#E8ECEF";
      const rotation = parseRotation(rect);

      if (width > 0 && height > 0) {
        rectangles.push({
          id: generateShapeId("rectangle"),
          type: "rectangle",
          x,
          y,
          width,
          height,
          color: fill,
          layerType: "floor",
          rotation
        });
      }
    }
  }

  return rectangles;
};

interface GroupBBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

const parentOf = (el: Element): Element | null =>
  el.parentNode && el.parentNode.nodeType === 1
    ? (el.parentNode as Element)
    : null;

/**
 * Approximate an element's bounding box by scanning coordinates in its
 * markup. Ignores transforms — used only when no live DOM is available
 * for exact `getBBox` measurement.
 */
const numericElementBBox = (root: Element): GroupBBox | null => {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  const consider = (x: number, y: number) => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  };

  const elements = [root, ...Array.from(root.querySelectorAll("*"))];
  for (const el of elements) {
    const tag = el.localName;
    const num = (attr: string) =>
      Number.parseFloat(el.getAttribute(attr) || "0");
    // Apply matrix/translate transforms from the element up to (and
    // including) the measured root — Figma places many primitives via
    // transform="matrix(...)" with no x/y attributes at all.
    const considerLocal = (x: number, y: number) => {
      let px = x;
      let py = y;
      let current: Element | null = el;
      while (current) {
        const matrix = parseTransformAsMatrix(current);
        if (matrix) {
          const transformed = applyMatrix(matrix, px, py);
          px = transformed.x;
          py = transformed.y;
        }
        if (current === root) break;
        current = parentOf(current);
      }
      consider(px, py);
    };
    if (tag === "rect" || tag === "image") {
      considerLocal(num("x"), num("y"));
      considerLocal(num("x") + num("width"), num("y"));
      considerLocal(num("x"), num("y") + num("height"));
      considerLocal(num("x") + num("width"), num("y") + num("height"));
    } else if (tag === "circle") {
      considerLocal(num("cx") - num("r"), num("cy") - num("r"));
      considerLocal(num("cx") + num("r"), num("cy") + num("r"));
    } else if (tag === "ellipse") {
      considerLocal(num("cx") - num("rx"), num("cy") - num("ry"));
      considerLocal(num("cx") + num("rx"), num("cy") + num("ry"));
    } else if (tag === "line") {
      considerLocal(num("x1"), num("y1"));
      considerLocal(num("x2"), num("y2"));
    } else if (tag === "path") {
      for (const seg of parseSvgPathToSegments(el.getAttribute("d") || "")) {
        considerLocal(seg.x1, seg.y1);
        considerLocal(seg.x2, seg.y2);
      }
    }
  }

  if (!Number.isFinite(minX) || !Number.isFinite(maxX)) return null;
  return {
    x: minX,
    y: minY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY)
  };
};

/**
 * Measure exact bounding boxes for elements of a parsed (detached) SVG by
 * temporarily mounting a hidden clone in the live document — `getBBox`
 * needs layout, and it accounts for nested transforms (rotated stair
 * rects etc.) that a plain coordinate scan cannot. Falls back to the
 * numeric scan per element outside a browser.
 */
const measureElementBBoxes = (
  svgRoot: Element,
  targets: Element[]
): Map<Element, GroupBBox> => {
  const result = new Map<Element, GroupBBox>();
  if (targets.length === 0) return result;

  const indexPath = (el: Element): number[] => {
    const path: number[] = [];
    let current: Element | null = el;
    while (current && current !== svgRoot) {
      const parent = parentOf(current);
      if (!parent) break;
      path.unshift(Array.prototype.indexOf.call(parent.children, current));
      current = parent;
    }
    return path;
  };

  if (typeof document !== "undefined" && document.body) {
    let host: HTMLDivElement | null = null;
    try {
      host = document.createElement("div");
      host.style.cssText =
        "position:absolute;left:-100000px;top:0;width:0;height:0;overflow:hidden;";
      const clone = document.importNode(svgRoot, true);
      host.appendChild(clone);
      document.body.appendChild(host);

      for (const target of targets) {
        let resolved: Element | null = clone as Element;
        for (const idx of indexPath(target)) {
          resolved = (resolved?.children?.[idx] as Element) ?? null;
          if (!resolved) break;
        }
        if (
          resolved &&
          typeof (resolved as SVGGraphicsElement).getBBox === "function"
        ) {
          try {
            const graphics = resolved as SVGGraphicsElement;
            const box = graphics.getBBox();
            if (box && (box.width > 0 || box.height > 0)) {
              // getBBox does NOT include the element's own `transform`
              // (nor its ancestors') — matrix-placed rects would measure
              // at the origin. Map the box corners through the
              // element→root-user-space matrix (screen CTMs cancel out
              // viewport/viewBox scaling) to get real map coordinates.
              const rootScreen = (clone as SVGSVGElement).getScreenCTM?.();
              const elementScreen = graphics.getScreenCTM?.();
              const ctm =
                rootScreen && elementScreen
                  ? rootScreen.inverse().multiply(elementScreen)
                  : null;
              if (
                ctm &&
                !(
                  ctm.a === 1 &&
                  ctm.b === 0 &&
                  ctm.c === 0 &&
                  ctm.d === 1 &&
                  ctm.e === 0 &&
                  ctm.f === 0
                )
              ) {
                const corners = [
                  [box.x, box.y],
                  [box.x + box.width, box.y],
                  [box.x, box.y + box.height],
                  [box.x + box.width, box.y + box.height]
                ];
                let minX = Number.POSITIVE_INFINITY;
                let minY = Number.POSITIVE_INFINITY;
                let maxX = Number.NEGATIVE_INFINITY;
                let maxY = Number.NEGATIVE_INFINITY;
                for (const [px, py] of corners) {
                  const tx = ctm.a * px + ctm.c * py + ctm.e;
                  const ty = ctm.b * px + ctm.d * py + ctm.f;
                  minX = Math.min(minX, tx);
                  minY = Math.min(minY, ty);
                  maxX = Math.max(maxX, tx);
                  maxY = Math.max(maxY, ty);
                }
                result.set(target, {
                  x: minX,
                  y: minY,
                  width: Math.max(1, maxX - minX),
                  height: Math.max(1, maxY - minY)
                });
              } else {
                result.set(target, {
                  x: box.x,
                  y: box.y,
                  width: Math.max(1, box.width),
                  height: Math.max(1, box.height)
                });
              }
            }
          } catch {
            // getBBox throws for non-rendered elements — fall back below
          }
        }
      }
    } catch {
      // DOM measurement unavailable — fall back below
    } finally {
      host?.remove();
    }
  }

  for (const target of targets) {
    if (!result.has(target)) {
      const fallback = numericElementBBox(target);
      if (fallback) result.set(target, fallback);
    }
  }
  return result;
};

const serializeElement = (el: Element): string => {
  if (typeof XMLSerializer !== "undefined") {
    return new XMLSerializer().serializeToString(el);
  }
  return el.outerHTML;
};

/**
 * Groups preserved verbatim as vector shapes. Everything the semantic
 * importers can't model (furniture, stairs, POI icons, outlined text
 * labels, the building silhouette) is kept pixel-perfect as individual
 * SVG shapes instead of being dropped — each stays movable/deletable in
 * the builder.
 */
const collectPreservedElements = (
  doc: Document
): { background: Element[]; overlay: Element[] } => {
  const background: Element[] = [];
  const overlay: Element[] = [];
  const seen = new Set<Element>();
  // The legacy in-map legend is skipped wholesale — the builder has its
  // own legend system, and the legend block's labels/swatches would
  // otherwise be caught by the label rules below.
  const isInsideLegend = (el: Element): boolean => {
    let current: Element | null = el;
    while (current) {
      if ((current.getAttribute("id") || "").startsWith("Legend")) return true;
      current = parentOf(current);
    }
    return false;
  };
  // Canvas size, for the huge-blob check below.
  const svgRoot = doc.querySelector("svg");
  const viewBox = (svgRoot?.getAttribute("viewBox") || "")
    .split(/[\s,]+/)
    .map(Number);
  const canvasWidth =
    viewBox[2] || Number.parseFloat(svgRoot?.getAttribute("width") || "0");
  const canvasHeight =
    viewBox[3] || Number.parseFloat(svgRoot?.getAttribute("height") || "0");

  // A multi-child group spanning most of the map (e.g. one "TVs" group
  // holding every TV on the floor) must not become a single shape — its
  // giant hit area would swallow every click on the canvas.
  const isHugeContainer = (el: Element): boolean => {
    if (el.localName !== "g" || canvasWidth <= 0 || canvasHeight <= 0) {
      return false;
    }
    if (el.children.length < 2) return false;
    const bbox = numericElementBBox(el);
    // Either dimension is enough — a wide strip of TVs (67% × 38% of the
    // canvas) blocks clicks along a whole band of the map just the same.
    return (
      !!bbox &&
      (bbox.width > canvasWidth * 0.55 || bbox.height > canvasHeight * 0.55)
    );
  };

  const push = (list: Element[], el: Element | null) => {
    if (!el || seen.has(el) || isInsideLegend(el)) return;
    if (list === overlay && isHugeContainer(el)) {
      for (const child of Array.from(el.children)) {
        push(list, child);
      }
      return;
    }
    seen.add(el);
    list.push(el);
  };

  // Building silhouette — the white floor cut-out behind everything
  push(background, doc.querySelector('path[id^="Background"]'));

  // Structural stairs (the POI stair icons are covered by the Icons rule)
  for (const g of Array.from(doc.querySelectorAll('g[id^="Stairs"]'))) {
    if ((g.getAttribute("id") || "").includes("Icon")) continue;
    push(overlay, g);
  }

  // Furniture — one shape per piece so each stays individually movable.
  // Nested "Furniture_2"-style containers are unpacked into their pieces
  // instead of becoming one canvas-sized shape.
  const isPureGroupContainer = (el: Element): boolean => {
    const kids = Array.from(el.children);
    const groups = kids.filter((c) => c.localName === "g");
    return groups.length >= 2 && groups.length === kids.length;
  };
  const PRIMITIVE_TAGS = new Set([
    "path",
    "rect",
    "circle",
    "ellipse",
    "line",
    "polygon",
    "polyline"
  ]);
  const pushVisualPieces = (group: Element) => {
    for (const el of Array.from(group.children)) {
      if (el.localName === "g") {
        const id = el.getAttribute("id") || "";
        // Unpack container-only groups (no primitives of their own) so a
        // furniture column doesn't become one canvas-sized shape; groups
        // that draw something themselves are one logical piece — keep
        // whole.
        if (
          id.startsWith("Furniture") ||
          (/^Group \d+/.test(id) && isPureGroupContainer(el))
        ) {
          pushVisualPieces(el);
        } else {
          push(overlay, el);
        }
      } else if (PRIMITIVE_TAGS.has(el.localName)) {
        // Standalone primitives drawn directly in the container — many
        // maps draw each desk surface as a bare path/rect here, not
        // inside a group. Preserve them one by one so every desk visual
        // shows up and stays individually movable.
        push(overlay, el);
      }
    }
  };
  for (const name of ["Furniture", "furniture"]) {
    const group = doc.querySelector(`g[id="${name}"]`);
    if (group) pushVisualPieces(group);
  }

  // Custom-shaped desk clusters ("Cubical Desks & Walls" and similar) —
  // bespoke vector drawings spanning several desks each, plus divider
  // rails drawn directly in the container. Preserved per piece so they
  // stay movable; the bookable desks are separate reservation-node spots
  // rendered on top.
  for (const g of Array.from(doc.querySelectorAll("g[id]"))) {
    const id = (g.getAttribute("id") || "").toLowerCase();
    if (/cubical|cubicle/.test(id)) {
      pushVisualPieces(g);
    }
  }

  // POI icons (restrooms, elevators, phone rooms, ...)
  for (const name of ["Icons", "icons"]) {
    const group = doc.querySelector(`g[id="${name}"]`);
    if (group) {
      for (const el of childGroups(group)) push(overlay, el);
    }
  }

  // Structural fills the rectangle importer can't model: matrix/
  // translate/off-center-rotated rects (desk visuals whose position
  // lives in the transform — they'd pile up at the origin), freeform
  // filler paths, and round primitives. Preserved individually so each
  // desk surface stays movable (simple ones convert to native shapes
  // downstream). Door paths are wall openings, not visuals. "Filler"
  // matches its numbered variants too.
  const fillContainers = [
    doc.querySelector('g[id="Layered"]'),
    ...Array.from(doc.querySelectorAll("g[id]")).filter((g) =>
      /^filler/i.test(g.getAttribute("id") || "")
    )
  ].filter(Boolean) as Element[];
  for (const group of fillContainers) {
    for (const el of Array.from(group.querySelectorAll("rect"))) {
      if (needsVectorPreservation(el)) {
        push(overlay, el);
      }
    }
    for (const el of Array.from(group.querySelectorAll("path"))) {
      if (!hasAncestorGroupId(el, (id) => /^doors?/.test(id), group)) {
        push(overlay, el);
      }
    }
    for (const el of Array.from(
      group.querySelectorAll("circle,ellipse,line,polygon,polyline")
    )) {
      push(overlay, el);
    }
  }

  // Text labels — exported as outline paths, so not recoverable as
  // editable text, but preserved pixel-perfect
  for (const name of ["Labels", "labels"]) {
    const group = doc.querySelector(`g[id="${name}"]`);
    if (group) {
      for (const el of Array.from(group.children)) push(overlay, el);
    }
  }
  for (const g of Array.from(doc.querySelectorAll("g[id]"))) {
    const id = g.getAttribute("id") || "";
    if (/^(Meeting Room Label|Admin Label|Area Labels)/.test(id)) {
      for (const el of Array.from(g.children)) push(overlay, el);
    }
  }
  // Loose label paths drawn directly in Structure (area/room names).
  // Desk labels are excluded — the imported spots render their own.
  const isInsideExcludedLabelGroup = (el: Element): boolean => {
    let current = parentOf(el);
    while (current) {
      const id = (current.getAttribute("id") || "").toLowerCase();
      if (id.includes("desk label") || id === "resources") return true;
      current = parentOf(current);
    }
    return false;
  };
  for (const el of Array.from(doc.querySelectorAll('path[id^="Label"]'))) {
    if (!isInsideExcludedLabelGroup(el)) push(overlay, el);
  }

  // Meeting-room tables & chairs living inside the resources tree — the
  // spot importer only takes the circle, this keeps the room furniture
  const resources =
    doc.querySelector('g[id="resources"]') ||
    doc.querySelector('g[id="Resources"]');
  if (resources) {
    for (const g of Array.from(
      resources.querySelectorAll('g[id^="Table and Chairs"]')
    )) {
      push(overlay, g);
    }
  }

  // NOTE: the legacy "Legend" group is intentionally skipped — the
  // builder has its own legend system.

  // Drop targets nested inside another target — the parent's markup
  // already contains them.
  const all = new Set([...background, ...overlay]);
  const isNested = (el: Element) => {
    let p = parentOf(el);
    while (p) {
      if (all.has(p)) return true;
      p = parentOf(p);
    }
    return false;
  };
  return {
    background: background.filter((el) => !isNested(el)),
    overlay: overlay.filter((el) => !isNested(el))
  };
};

const PRESERVE_PADDING = 2;

/** Presentation attributes that cascade in SVG. When a group is lifted
 *  out of the document, whatever its ancestors declared must move onto
 *  the wrapper — the source root's fill="none" is the critical one:
 *  without it, every stroke-only path re-defaults to a black fill. */
const INHERITED_PRESENTATION_ATTRS = [
  "fill",
  "fill-opacity",
  "fill-rule",
  "clip-rule",
  "stroke",
  "stroke-width",
  "stroke-opacity",
  "opacity",
  "color"
];

const inheritedWrapperAttrs = (el: Element): string => {
  let attrs = "";
  for (const attr of INHERITED_PRESENTATION_ATTRS) {
    // The element's own value already serializes with it — only look up
    // the ancestor chain (nearest ancestor wins, like the CSS cascade).
    let current = parentOf(el);
    while (current) {
      const value = current.getAttribute(attr);
      if (value) {
        attrs += ` ${attr}="${value}"`;
        break;
      }
      current = parentOf(current);
    }
  }
  return attrs;
};

const createPreservedSvgShape = (el: Element, bbox: GroupBBox): SVGShape => {
  const x = bbox.x - PRESERVE_PADDING;
  const y = bbox.y - PRESERVE_PADDING;
  const width = bbox.width + PRESERVE_PADDING * 2;
  const height = bbox.height + PRESERVE_PADDING * 2;
  const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${x} ${y} ${width} ${height}" width="${width}" height="${height}"${inheritedWrapperAttrs(el)}>${serializeElement(el)}</svg>`;
  return {
    id: generateShapeId("svg"),
    type: "svg",
    x,
    y,
    width,
    height,
    path: "",
    svgContent,
    stroke: "",
    strokeWidth: 0,
    layerType: "floor",
    layerLabel: el.getAttribute("id") || undefined
  };
};

interface ComposedMatrix {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

const IDENTITY_MATRIX: ComposedMatrix = {
  a: 1,
  b: 0,
  c: 0,
  d: 1,
  e: 0,
  f: 0
};

const multiplyMatrix = (
  m1: ComposedMatrix,
  m2: ComposedMatrix
): ComposedMatrix => ({
  a: m1.a * m2.a + m1.c * m2.b,
  b: m1.b * m2.a + m1.d * m2.b,
  c: m1.a * m2.c + m1.c * m2.d,
  d: m1.b * m2.c + m1.d * m2.d,
  e: m1.a * m2.e + m1.c * m2.f + m1.e,
  f: m1.b * m2.e + m1.d * m2.f + m1.f
});

/** Compose a transform list of matrix()/translate()/rotate() into one
 *  matrix. Returns null for anything else (scale/skew) — those can't be
 *  represented by native shapes. */
const parseComposedTransform = (
  transform: string | null
): ComposedMatrix | null => {
  if (!transform || !transform.trim()) return IDENTITY_MATRIX;
  let matrix = IDENTITY_MATRIX;
  const ops = [...transform.matchAll(/([a-zA-Z]+)\(([^)]*)\)/g)];
  if (ops.length === 0) return null;
  for (const [, fn, argsStr] of ops) {
    const args = argsStr
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number);
    let op: ComposedMatrix;
    if (fn === "matrix" && args.length === 6) {
      op = {
        a: args[0],
        b: args[1],
        c: args[2],
        d: args[3],
        e: args[4],
        f: args[5]
      };
    } else if (fn === "translate") {
      op = { ...IDENTITY_MATRIX, e: args[0] || 0, f: args[1] || 0 };
    } else if (fn === "rotate") {
      const rad = ((args[0] || 0) * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const cx = args[1] || 0;
      const cy = args[2] || 0;
      op = {
        a: cos,
        b: sin,
        c: -sin,
        d: cos,
        e: cx - cos * cx + sin * cy,
        f: cy - sin * cx - cos * cy
      };
    } else {
      return null;
    }
    matrix = multiplyMatrix(matrix, op);
  }
  return matrix;
};

/** Compose the transform chain from an element up to (and including) an
 *  ancestor. Unparseable transforms (scale/skew) are treated as identity
 *  — same leniency the legacy matrix-only walk had. */
const composeTransformChain = (
  el: Element,
  stopAt: Element
): ComposedMatrix => {
  let matrix = IDENTITY_MATRIX;
  let current: Element | null = el;
  while (current) {
    const own = parseComposedTransform(current.getAttribute("transform"));
    if (own) matrix = multiplyMatrix(own, matrix);
    if (current === stopAt) break;
    current = parentOf(current);
  }
  return matrix;
};

/** Rotation + translation (possibly mirrored) — shape sizes survive. */
const isOrthonormal = (m: ComposedMatrix): boolean =>
  Math.abs(m.a * m.a + m.b * m.b - 1) < 0.02 &&
  Math.abs(m.c * m.c + m.d * m.d - 1) < 0.02 &&
  Math.abs(m.a * m.c + m.b * m.d) < 0.02;

const applyComposed = (m: ComposedMatrix, x: number, y: number) => ({
  x: m.a * x + m.c * y + m.e,
  y: m.b * x + m.d * y + m.f
});

/** Fill/stroke attributes for native shapes, with the SVG cascade for
 *  fill (many primitives inherit it from an ancestor group). */
const nativeStyleOf = (el: Element) => {
  let fill: string | null = el.getAttribute("fill");
  if (fill === null) {
    let current = parentOf(el);
    while (current && fill === null) {
      fill = current.getAttribute("fill");
      current = parentOf(current);
    }
  }
  const stroke = el.getAttribute("stroke");
  const strokeWidth = Number.parseFloat(el.getAttribute("stroke-width") || "1");
  const hasFill = !!fill && fill !== "none";
  const hasStroke = !!stroke && stroke !== "none";
  return {
    color: hasFill ? (fill as string) : undefined,
    noFill: hasFill ? undefined : true,
    stroke: hasStroke ? (stroke as string) : undefined,
    strokeWidth: hasStroke ? strokeWidth : undefined
  };
};

const rectFromGeometry = (
  el: Element,
  x: number,
  y: number,
  width: number,
  height: number,
  cornerRadius: number,
  matrix: ComposedMatrix
): RectangleShape | null => {
  if (width <= 0 || height <= 0 || !isOrthonormal(matrix)) return null;
  const p00 = applyComposed(matrix, x, y);
  const p10 = applyComposed(matrix, x + width, y);
  const p01 = applyComposed(matrix, x, y + height);
  const wVec = { x: p10.x - p00.x, y: p10.y - p00.y };
  const hVec = { x: p01.x - p00.x, y: p01.y - p00.y };
  // Mirrored placements (negative cross) fold onto the congruent
  // rotated rect drawn from the opposite corner.
  const mirrored = wVec.x * hVec.y - wVec.y * hVec.x < 0;
  const origin = mirrored ? p01 : p00;
  let rotation = (Math.atan2(wVec.y, wVec.x) * 180) / Math.PI;
  if (Math.abs(rotation) < 0.05) rotation = 0;
  return {
    id: generateShapeId("rectangle"),
    type: "rectangle",
    x: origin.x,
    y: origin.y,
    width,
    height,
    cornerRadius: cornerRadius > 0 ? cornerRadius : undefined,
    rotation: rotation !== 0 ? rotation : undefined,
    ...nativeStyleOf(el),
    layerType: "floor",
    layerLabel: el.getAttribute("id") || undefined
  };
};

/** A closed 4-segment axis-aligned path is just a rectangle. */
const rectPathGeometry = (
  d: string
): { x: number; y: number; width: number; height: number } | null => {
  const segments = parseSvgPathToSegments(d);
  if (segments.length !== 4) return null;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const seg of segments) {
    if (seg.curvature) return null;
    if (seg.x1 !== seg.x2 && seg.y1 !== seg.y2) return null;
    minX = Math.min(minX, seg.x1, seg.x2);
    minY = Math.min(minY, seg.y1, seg.y2);
    maxX = Math.max(maxX, seg.x1, seg.x2);
    maxY = Math.max(maxY, seg.y1, seg.y2);
  }
  const first = segments[0];
  const last = segments[segments.length - 1];
  if (first.x1 !== last.x2 || first.y1 !== last.y2) return null;
  // Each side must span the full bbox edge — an L/zigzag also has 4
  // axis-aligned segments but isn't a rectangle.
  const width = maxX - minX;
  const height = maxY - minY;
  for (const seg of segments) {
    const len = Math.hypot(seg.x2 - seg.x1, seg.y2 - seg.y1);
    if (Math.abs(len - width) > 0.5 && Math.abs(len - height) > 0.5) {
      return null;
    }
  }
  return { x: minX, y: minY, width, height };
};

/**
 * A closed all-curve path whose arc endpoints AND arc midpoints all sit
 * at the same distance from their center is a circle. Checking distance
 * (not N/E/S/W bbox midpoints) keeps it rotation-invariant: Figma bakes a
 * rotated desk's angle into its reservation-dot path, so the arcs start
 * off-axis. Rounded rects contain straight sides, so they never match.
 */
const circlePathGeometry = (
  d: string
): { cx: number; cy: number; r: number } | null => {
  const segments = parseSvgPathToSegments(d);
  if (segments.length < 2 || segments.length > 8) return null;
  if (!segments.every((seg) => seg.curvature)) return null;
  const first = segments[0];
  const last = segments[segments.length - 1];
  if (
    Math.hypot(first.x1 - last.x2, first.y1 - last.y2) > 1.5 ||
    segments.some((seg) => seg.subpath !== first.subpath)
  ) {
    return null;
  }

  // Each arc's start point plus its on-curve midpoint — the chord
  // midpoint pushed out by the curvature along the normal (-dy, dx).
  const samples: { x: number; y: number }[] = [];
  for (const seg of segments) {
    const dx = seg.x2 - seg.x1;
    const dy = seg.y2 - seg.y1;
    const len = Math.hypot(dx, dy);
    const curvature = seg.curvature ?? 0;
    samples.push({ x: seg.x1, y: seg.y1 });
    samples.push({
      x: (seg.x1 + seg.x2) / 2 + (curvature * -dy) / len,
      y: (seg.y1 + seg.y2) / 2 + (curvature * dx) / len
    });
  }

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const { x, y } of samples) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const distances = samples.map(({ x, y }) => Math.hypot(x - cx, y - cy));
  const r = distances.reduce((sum, dist) => sum + dist, 0) / distances.length;
  if (!(r > 0)) return null;
  const tolerance = Math.max(2, r * 0.05);
  if (distances.some((dist) => Math.abs(dist - r) > tolerance)) return null;
  return { cx, cy, r };
};

/**
 * Try to express a preserved element as a native rectangle/circle shape —
 * native shapes support the builder's color/stroke editing out of the
 * box, unlike rasterized vector content. Handles bare rects/circles,
 * rect-shaped paths, and single-child wrapper groups; anything more
 * complex stays a preserved vector shape.
 */
const tryConvertPreservedToNative = (
  el: Element,
  parentMatrix: ComposedMatrix = IDENTITY_MATRIX
): Shape | null => {
  const ownMatrix = parseComposedTransform(el.getAttribute("transform"));
  if (!ownMatrix) return null;
  const matrix = multiplyMatrix(parentMatrix, ownMatrix);

  if (el.localName === "g") {
    const kids = Array.from(el.children);
    if (kids.length !== 1) return null;
    const converted = tryConvertPreservedToNative(kids[0], matrix);
    if (converted && !converted.layerLabel) {
      converted.layerLabel = el.getAttribute("id") || undefined;
    }
    return converted;
  }

  const num = (attr: string) => Number.parseFloat(el.getAttribute(attr) || "0");

  if (el.localName === "rect") {
    return rectFromGeometry(
      el,
      num("x"),
      num("y"),
      num("width"),
      num("height"),
      num("rx"),
      matrix
    );
  }

  if (el.localName === "circle") {
    const r = num("r");
    if (r <= 0 || !isOrthonormal(matrix)) return null;
    const center = applyComposed(matrix, num("cx"), num("cy"));
    return {
      id: generateShapeId("circle"),
      type: "circle",
      x: center.x,
      y: center.y,
      radius: r,
      width: r * 2,
      height: r * 2,
      ...nativeStyleOf(el),
      layerType: "floor",
      layerLabel: el.getAttribute("id") || undefined
    };
  }

  if (el.localName === "path") {
    const d = el.getAttribute("d") || "";
    const geometry = rectPathGeometry(d);
    if (geometry) {
      return rectFromGeometry(
        el,
        geometry.x,
        geometry.y,
        geometry.width,
        geometry.height,
        0,
        matrix
      );
    }
    const circle = circlePathGeometry(d);
    if (circle && isOrthonormal(matrix)) {
      const center = applyComposed(matrix, circle.cx, circle.cy);
      return {
        id: generateShapeId("circle"),
        type: "circle",
        x: center.x,
        y: center.y,
        radius: circle.r,
        width: circle.r * 2,
        height: circle.r * 2,
        ...nativeStyleOf(el),
        layerType: "floor",
        layerLabel: el.getAttribute("id") || undefined
      };
    }
    return null;
  }

  return null;
};

const parsePreservedVectorShapes = (
  doc: Document
): { background: Shape[]; overlay: Shape[] } => {
  const svgRoot = doc.querySelector("svg");
  if (!svgRoot) return { background: [], overlay: [] };
  const { background, overlay } = collectPreservedElements(doc);

  // Simple primitives become native shapes (editable colors); the rest
  // need bbox measurement for verbatim vector preservation.
  const natives = new Map<Element, Shape>();
  for (const el of [...background, ...overlay]) {
    const native = tryConvertPreservedToNative(el);
    if (native) natives.set(el, native);
  }
  const bboxes = measureElementBBoxes(
    svgRoot,
    [...background, ...overlay].filter((el) => !natives.has(el))
  );
  const toShapes = (els: Element[]): Shape[] =>
    els.flatMap((el) => {
      const native = natives.get(el);
      if (native) return [native];
      const bbox = bboxes.get(el);
      return bbox ? [createPreservedSvgShape(el, bbox)] : [];
    });
  return { background: toShapes(background), overlay: toShapes(overlay) };
};

/**
 * Compute editing zone dimensions from SVG viewBox or element positions.
 */
const computeEditingZone = (
  doc: Document,
  shapes: Shape[]
): { width: number; height: number } => {
  const svg = doc.querySelector("svg");
  const MARGIN = 100;

  // Try viewBox first
  if (svg) {
    const viewBox = svg.getAttribute("viewBox");
    if (viewBox) {
      const parts = viewBox.split(/[\s,]+/).map(Number);
      if (parts.length === 4) {
        const vbWidth = parts[2];
        const vbHeight = parts[3];
        return {
          width: Math.min(vbWidth + MARGIN * 2, EDITING_ZONE_MAX_WIDTH),
          height: Math.min(vbHeight + MARGIN * 2, EDITING_ZONE_MAX_HEIGHT)
        };
      }
    }

    // Try width/height attributes
    const svgWidth = Number.parseFloat(svg.getAttribute("width") || "0");
    const svgHeight = Number.parseFloat(svg.getAttribute("height") || "0");
    if (svgWidth > 0 && svgHeight > 0) {
      return {
        width: Math.min(svgWidth + MARGIN * 2, EDITING_ZONE_MAX_WIDTH),
        height: Math.min(svgHeight + MARGIN * 2, EDITING_ZONE_MAX_HEIGHT)
      };
    }
  }

  // Fallback: compute from shape bounding box
  let maxX = 2200;
  let maxY = 1800;
  for (const shape of shapes) {
    if (shape.type === "wall") {
      maxX = Math.max(maxX, shape.x, shape.x2);
      maxY = Math.max(maxY, shape.y, shape.y2);
    } else {
      maxX = Math.max(maxX, shape.x + shape.width);
      maxY = Math.max(maxY, shape.y + shape.height);
    }
  }

  return {
    width: Math.min(maxX + MARGIN * 2, EDITING_ZONE_MAX_WIDTH),
    height: Math.min(maxY + MARGIN * 2, EDITING_ZONE_MAX_HEIGHT)
  };
};

/**
 * Convert a legacy SVG map string into a MapCanvasValue for the Map Builder.
 */
export const convertSvgToMapCanvasValue = (svgText: string): MapCanvasValue => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgText, "image/svg+xml");

  // Check for parse errors
  const parseError = doc.querySelector("parsererror");
  if (parseError) {
    throw new Error(`SVG parse error: ${parseError.textContent}`);
  }

  // Parse all shape types
  const walls = parseSvgWallsAndDoors(doc);
  const spots = parseSvgSpots(doc);
  const rectangles = parseSvgRectangles(doc);
  const preserved = parsePreservedVectorShapes(doc);

  // Stacking: silhouette at the very back, then floor rects, then the
  // preserved furniture/icons/labels, then walls, spots on top.
  const shapes: Shape[] = [
    ...preserved.background,
    ...rectangles,
    ...preserved.overlay,
    ...walls,
    ...spots
  ];

  const { width, height } = computeEditingZone(doc, shapes);

  const spotLabelPattern: SpotLabelPatternItem[] = [
    { type: "text", value: "" },
    { type: "numbers", depth: 9999 }
  ];

  return {
    version: MAP_BUILDER_VERSION,
    lastUpdate: new Date().toISOString(),
    gridSize: 20,
    editingZoneWidth: width,
    editingZoneHeight: height,
    shapes,
    spotLabelPattern,
    lastSpotLabel: null
  };
};
